
import { Component, Provide, Vue, Watch } from "vue-property-decorator";
import { namespace, Getter, Action } from "vuex-class";
import Sessions from "@/components/Sessions.vue";
import ViewController from "@/components/views/ViewController.vue";
import ViewSidebar from "@/components/views/ViewSidebar.vue";
import ViewHeader from "@/components/views/ViewHeader.vue";
import { ViewMode, ViewObject } from "@/store/views/types.ts";
import { ResizeObserver } from "vue-resize";
import "vue-resize/dist/vue-resize.css";
import { FeaturesList } from "@/store/types";
import { get } from "lodash";
import ViewsEventBus from "@/services/views/viewsEventBus";
import vClickOutside from "v-click-outside";
import ManualRaise from "@/components/ManualRaise.vue";
import ProcessingError from "@/components/ProcessingError.vue";

/**
 * Expose our framework modules globally, so our view plugins can access them
 * without needing to bundle them with the plugin itself.
 **/
import "@/scripts/WindowExports";

const ViewState = namespace("views");

@Component({
	components: {
		"view-header": ViewHeader,
		session: Sessions,
		"view-controller": ViewController,
		"side-bar": ViewSidebar,
		"resize-observer": ResizeObserver,
		"manual-raise": ManualRaise,
		"processing-error": ProcessingError,
	},
	directives: {
		clickOutside: vClickOutside.directive,
	},
})
export default class Views extends Vue {
	@ViewState.Action loadPluginTypes: any;
	@ViewState.Mutation setSideBarExtended: any;
	@ViewState.Getter getSideBarExtended: boolean;
	@ViewState.Getter getSideBarPinned: boolean;
	@ViewState.Getter getViewMode: ViewMode;
	@ViewState.Getter("getUserViews") userViews: ViewObject[];

	@Getter("getFeaturesList") featuresList: FeaturesList;

	@Provide("viewsEventBus") private eventBus: ViewsEventBus = new ViewsEventBus();

	private readonly SidebarWidth = 350;

	private gridHeight: number = 720;
	private gridWidth: number = 1280;

	private viewIDOverride: number = 0;
	private viewPageNumber: number = 0;
	private viewGUID: string = "";

	$refs!: {
		gridContainer: HTMLDivElement;
		headerContainer: HTMLDivElement;
	};

	// If feature is on / off
	public get isViewsEnabled() {
		return get(this.featuresList, ["Views"]);
	}

	public get isSideBarPinned() {
		return this.getSideBarPinned && this.getViewMode == ViewMode.None;
	}

	public async mounted() {
		if (this.isViewsEnabled) {
			await this.loadPluginTypes();
			this.resizeGrid();
		}

		this.loadWindowInfo();

		// Setup our viewguid to be the guid of our eventbus if we are the main views window.
		if (this.isMainViewWindow) {
			this.viewGUID = this.eventBus.eventBusGUID;
		}
		else {
			this.eventBus.setupBroadCastChannel(this.viewGUID);
		}

	}

	public get isMainViewWindow(): boolean {
		return this.viewPageNumber === 0 && this.viewIDOverride === 0;
	}

	public loadWindowInfo() {
		if (this.$route.params.viewid) {
			this.viewIDOverride = parseInt(this.$route.params.viewid);
			
		}

		if (this.$route.params.pageNumber) {
			this.viewPageNumber = parseInt(this.$route.params.pageNumber);
		}

		if (this.$route.query["guid"]) {
			this.viewGUID = this.$route.query["guid"].toString();				
		}

	}

	public async resizeGrid() {
		this.gridHeight = this.$refs.gridContainer.clientHeight;
		this.gridWidth = this.$refs.gridContainer.clientWidth;
	}

	public async clickOutsideSidebar(event) {
		if (!this.getSideBarPinned) this.setSideBarExtended(false);
	}

	@Watch("getViewMode")
	public editModeChanged(value: ViewMode) {
		this.resizeGrid();
		if (value == ViewMode.None) {
			this.gridHeight += 60;

			if (this.getSideBarPinned) {
				this.setSideBarExtended(true);
				this.gridWidth -= this.SidebarWidth;
			}
		} else {
			this.gridHeight -= 60;
			this.setSideBarExtended(false);

			if (this.getSideBarPinned) {
				this.gridWidth += this.SidebarWidth;
			}
		}
	}

	@Watch("getSideBarExtended")
	public sideBarExtendedChanged(value: boolean) {
		this.resizeGrid();
		if (this.getSideBarPinned) {
			if (value) {
				this.gridWidth -= this.SidebarWidth;
			} else {
				this.gridWidth += this.SidebarWidth;
			}
		}
	}

	@Watch("getSideBarPinned")
	public sideBarPinnedChanged(value: boolean) {
		this.resizeGrid();
		if (value) {
			this.gridWidth -= this.SidebarWidth;
		} else {
			this.gridWidth += this.SidebarWidth;
		}
	}
}
